import React, { useState, useEffect, useRef } from "react";
import MarkAsMenu from "./MarkAsMenu";
import config from "../config";
import { FioriButton, StatusPill, Dialog } from "./FioriUI";

/* ───────────────── Notes Panel ───────────────── */
const NotesPanel = ({ invoiceId, userEmail, notes, setNotes }) => {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const textareaRef = useRef(null);

  const API_BASE = config.api.exceptions.replace('/exceptions', '');

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/exception-notes?invoice_id=${invoiceId}`)
      .then((res) => res.json())
      .then((data) => {
        const parsed = data.notes || data.body || data;
        setNotes(Array.isArray(parsed) ? parsed : []);
      })
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const handleSave = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const payload = {
        invoice_id: invoiceId,
        note_text: draft,
        created_by: userEmail,
      };
      const res = await fetch(`${API_BASE}/exception-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      const newNote = {
        id: data.id,
        note_text: draft,
        created_by: userEmail,
        created_at: data.created_at,
      };
      setNotes((prev) => [newNote, ...prev]);
      setDraft("");
      textareaRef.current?.focus();
    } catch (err) {
      console.error("Error saving note:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-3 border-b border-fiori-border-light bg-fiori-page-2/40 flex items-center gap-2">
        <svg
          className="w-4 h-4 text-fiori-brand"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
        <h2 className="text-sm font-semibold text-fiori-text">Notes</h2>
      </div>

      <div className="p-4 border-b border-fiori-border-light">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a note..."
          rows={3}
          className="fiori-input resize-none"
        />
        <div className="flex justify-end mt-2">
          <FioriButton
            variant="emphasized"
            onClick={handleSave}
            disabled={!draft.trim() || saving}
            loading={saving}
          >
            {saving ? "Saving..." : "Save Note"}
          </FioriButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <p className="text-center text-fiori-text-secondary text-xs py-4">
            Loading...
          </p>
        ) : notes.length === 0 ? (
          <p className="text-center text-fiori-text-disabled text-xs py-4">
            No notes yet.
          </p>
        ) : (
          notes.map((n) => (
            <div
              key={n.id}
              className="border border-fiori-border-light rounded-fiori p-2 text-xs bg-white"
            >
              <p className="text-fiori-text">{n.note_text}</p>
              <p className="text-[11px] text-fiori-text-secondary mt-1">
                {n.created_by} •{" "}
                {n.created_at
                  ? new Date(n.created_at).toLocaleString()
                  : ""}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/* ───────────────── Main Viewer ───────────────── */
const PDFViewerPage = ({
  invoice,
  onClose,
  userEmail = "user@example.com",
  onStatusUpdate,
}) => {
  const [modal, setModal] = useState(null);
  const [status, setStatus] = useState(invoice.status);
  const [updating, setUpdating] = useState(false);
  const [notes, setNotes] = useState([]);
  const [declineReason, setDeclineReason] = useState("");

  const API_BASE = config.api.exceptions.replace('/exceptions', '');

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleMarkAs = (option) => {
    console.log("📝 Mark as selected:", option);
  };

  const handleStatusUpdate = async (newStatus, reason = "") => {
    setUpdating(true);
    try {
      const payload = {
        invoice_id: invoice.id,
        status: newStatus,
        updated_by: userEmail,
        decline_reason: reason,
        s3_path: invoice.s3Path || invoice.pdfUrl,
      };
      if (newStatus === "DECLINED" && notes.length > 0) {
        payload.notes = notes.map((n) => ({
          id: n.id,
          note_text: n.note_text,
          created_by: n.created_by,
          created_at: n.created_at,
        }));
      }
      const response = await fetch(`${API_BASE}/update-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        await response.json();
        setStatus(newStatus);
        setModal(null);
        setDeclineReason("");
        onStatusUpdate?.({ ...invoice, status: newStatus });
      } else {
        const err = await response.json();
        console.error("Status update failed:", err);
      }
    } catch (e) {
      console.error("Status update error:", e);
    } finally {
      setUpdating(false);
    }
  };

  const lockedStatuses = [
    "exception",
    "paid",
    "credit memo created",
    "invoice cancelled",
    "debit memo created",
  ];
  const finalStatuses = [
    "APPROVED",
    "DECLINED",
    "CORRECTION IN PROGRESS",
    "SENT",
    "CREDIT MEMO CREATED",
    "INVOICE CANCELLED",
  ];

  return (
    <div className="fixed inset-0 z-50 bg-fiori-page flex flex-col overflow-hidden">
      {/* Sub-header (Object Page bar) */}
      <div className="flex justify-between items-center px-4 py-2 bg-white border-b border-fiori-border-light">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-sm font-medium text-fiori-brand hover:underline"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
          <div className="w-px h-4 bg-fiori-border" />
          <span className="font-semibold text-fiori-text text-sm truncate">
            INV-{invoice.id}
          </span>
          <StatusPill status={status} />
        </div>

        {!lockedStatuses.includes(status?.toLowerCase()) && (
          <div className="flex gap-2 items-center">
            {status !== "UNDER REVIEW" && (
              <MarkAsMenu
                onSelect={handleMarkAs}
                invoice={invoice}
                userEmail={userEmail}
                onStatusUpdate={handleStatusUpdate}
              />
            )}
            {!finalStatuses.includes(status) && (
              <>
                <FioriButton
                  variant="negative"
                  onClick={() => setModal("decline")}
                  disabled={updating}
                >
                  {updating ? "Processing..." : "Decline"}
                </FioriButton>
                <FioriButton
                  variant="emphasized"
                  onClick={() => setModal("approve")}
                  disabled={updating}
                >
                  {updating ? "Processing..." : "Approve"}
                </FioriButton>
              </>
            )}
          </div>
        )}
      </div>

      {/* Body: PDF + Notes (Fiori split layout) */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-[7] bg-fiori-page-2 overflow-hidden">
          <iframe
            src={invoice.pdfUrl}
            title={`Invoice ${invoice.id}`}
            className="w-full h-full border-0 bg-white"
          />
        </div>

        <div className="w-px bg-fiori-border-light flex-shrink-0" />

        <div className="flex-[3] min-w-[300px] max-w-[420px] overflow-hidden border-l border-fiori-border-light">
          <NotesPanel
            invoiceId={invoice.id}
            userEmail={userEmail}
            notes={notes}
            setNotes={setNotes}
          />
        </div>
      </div>

      {/* Approve/Decline confirmation dialog */}
      <Dialog
        show={!!modal}
        title={modal === "approve" ? "Approve Invoice" : "Decline Invoice"}
        size="sm"
        onClose={() => {
          setModal(null);
          setDeclineReason("");
        }}
        footer={
          <>
            <FioriButton
              onClick={() => {
                setModal(null);
                setDeclineReason("");
              }}
            >
              Cancel
            </FioriButton>
            {modal === "approve" ? (
              <FioriButton
                variant="emphasized"
                onClick={() => handleStatusUpdate("APPROVED")}
                disabled={updating}
                loading={updating}
              >
                {updating ? "Processing..." : "Approve"}
              </FioriButton>
            ) : (
              <FioriButton
                variant="negative"
                onClick={() => handleStatusUpdate("DECLINED", declineReason)}
                disabled={updating}
                loading={updating}
              >
                {updating ? "Processing..." : "Decline"}
              </FioriButton>
            )}
          </>
        }
      >
        <div className="px-4 py-4 space-y-3">
          <p className="text-sm text-fiori-text">
            {modal === "approve"
              ? `Approve invoice INV-${invoice.id}?`
              : `Decline invoice INV-${invoice.id}? Add an optional reason below.`}
          </p>
          {modal === "decline" && (
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason for declining (optional)"
              rows={3}
              className="fiori-input resize-none"
            />
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default PDFViewerPage;
